import { defineStore } from 'pinia';
import { useColorSchemeStylesheetStore } from './stylesheet-store';
import { until, useColorMode } from '@vueuse/core';
import { type ColorScheme, colorSchemeClasses } from './color-schemes';
import { computed, ref, nextTick, type WritableComputedRef } from 'vue';

export const useColorSchemeStore = defineStore('color-scheme', () => {
    const next = useColorMode({
        modes: colorSchemeClasses,
        async onChanged(scheme, defaultOnChanged) {
            await wait();
            currentScheme.value = scheme;
            defaultOnChanged(scheme);
        },
    });

    async function wait() {
        await nextTick();
        await until(loading).toBe(false, { timeout: 500 });
    }

    const stylesheets = useColorSchemeStylesheetStore();

    const loading = computed(() => stylesheets.loading);

    const currentScheme = ref<ColorScheme>(next.value as ColorScheme);

    const scheme = computed<ColorScheme>({
        get: () => currentScheme.value,
        set: (newValue) => {
            next.value = newValue;
        },
    });

    const auto = computed<boolean>({
        get: () => next.store.value === 'auto',
        set: (newValue) => {
            if (newValue) {
                next.value = 'auto';
            } else if (next.store.value === 'auto') {
                next.value = next.state.value;
            }
        },
    });

    async function set(
        nextScheme: ColorScheme | 'auto' | null | undefined
    ): Promise<ColorScheme> {
        nextScheme ??= 'auto';
        if (next.store.value !== nextScheme) {
            next.value = nextScheme;
            await wait();
        }
        return next.value as ColorScheme;
    }

    function reset(): Promise<ColorScheme> {
        return set('auto');
    }

    return {
        next: next.state as WritableComputedRef<ColorScheme>,
        scheme,
        system: next.system,
        store: next.store,
        auto,
        loading,
        wait,
        set,
        reset,
    };
});
